import { useEffect, useRef, useState } from 'react';
import { debounce, throttle } from 'lodash-es';
import { Popup, useMap } from 'react-map-gl';
import { useSetAtom, useAtomValue } from 'jotai';
import type { MapMouseEvent, MapboxGeoJSONFeature } from 'mapbox-gl';
// The shared `.popup` class, as the documents and wells popups use.
import mapStyles from 'components/map/map.module.scss';
import { formatNumber } from 'utils/number';
import { parseMomentUtc, US_DATE_FORMAT } from 'utils/dates';
import {
  formatRangeValue,
  valueRangeLabel,
} from '../filter-modal/value-histogram';
import { allDatasetAtoms } from '../atoms';
import { subsurfaceMapsConfig } from '../datasets/subsurface-maps.config';
import {
  DATASET_LAYER_PREFIX,
  docLayerId,
  styleLayerIds,
} from '../map-section/tile-layer-utils';
import {
  selectedSubsurfaceMapAtom,
  subsurfaceRasterOnMapIdAtom,
} from './atoms';
import { CELLS_LAYER_ID } from './utils';
import { AREAS_LAYER_IDS } from './layer-order';
import type { SubsurfaceMapRow } from './types';
import { PopupCard } from '../map-section/popup-card';

const datasetAtoms = allDatasetAtoms.subsurfaceMaps;

// The footprint fill is the hover/click target; the outline is too thin to hit.
const FILL_LAYER_ID = subsurfaceMapsConfig.tileLayers?.[0]
  ? docLayerId(subsurfaceMapsConfig.tileLayers[0], 0)
  : '';

// The documents and wells popups throttle their hover at the same 50 ms.
const HOVER_THROTTLE_MS = 50;
const CURSOR_CLAIM_DELAY_MS = HOVER_THROTTLE_MS + 10;

/** The hovered footprint of a map that isn't the selected one: its catalogue card. */
interface FootprintCard {
  kind: 'footprint';
  id: string;
  lngLat: [number, number];
  name: string;
  mapType: string;
  source: string;
  basin: string | null;
  geologicInterval: string | null;
  valueMin: number | null;
  valueMax: number | null;
  units: string | null;
  layerType: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * The selected map's cell under the cursor: its value, read off the cell tile
 * itself (no request, so it follows the cursor instantly), and the location.
 * Zoomed out the tile's cells are coarser than the map's, so the value is the
 * nearest coarser cell's; zoomed in it is the map's own cell. Null on a cell
 * without a numeric value.
 */
interface CellCard {
  kind: 'cell';
  id: number;
  lngLat: [number, number];
  name: string;
  mapType: string;
  basin: string | null;
  valueMin: number | null;
  valueMax: number | null;
  units: string | null;
  value: number | null;
}

type HoverCard = FootprintCard | CellCard;

/**
 * The map id a footprint feature carries.
 * @param {MapboxGeoJSONFeature} feature The footprint.
 * @return {string} The id, as the dataset's highlight keys it.
 */
const footprintId = (feature: MapboxGeoJSONFeature): string =>
  String(feature.properties?.id ?? feature.id ?? '');

/**
 * A tile property as text, or null when the feature doesn't carry it.
 * @param {unknown} value The property.
 * @return {string | null} The text.
 */
const textOrNull = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  return String(value);
};

/**
 * The card's fields off the tile feature itself, so a map outside the loaded
 * table page still has a card.
 * @param {MapboxGeoJSONFeature} feature The hovered footprint.
 * @param {[number, number]} lngLat Where the card anchors.
 * @return {FootprintCard} The card.
 */
function cardFrom(
  feature: MapboxGeoJSONFeature,
  lngLat: [number, number]
): FootprintCard {
  const properties = feature.properties ?? {};
  return {
    kind: 'footprint',
    id: footprintId(feature),
    lngLat,
    name: String(properties.name ?? ''),
    mapType: String(properties.map_type ?? ''),
    source: String(properties.source ?? ''),
    basin: textOrNull(properties.basin),
    geologicInterval: textOrNull(properties.geologic_interval),
    valueMin:
      properties.value_min === undefined ? null : Number(properties.value_min),
    valueMax:
      properties.value_max === undefined ? null : Number(properties.value_max),
    units: textOrNull(properties.units),
    layerType: textOrNull(properties.layer_type),
    createdAt: textOrNull(properties.created_at),
    updatedAt: textOrNull(properties.updated_at),
  };
}

/**
 * The selected map's card, off its table row and the hovered cell.
 * @param {SubsurfaceMapRow} row The selected map.
 * @param {MapboxGeoJSONFeature} cell The cell under the cursor.
 * @param {[number, number]} lngLat Where the card anchors.
 * @return {CellCard} The card.
 */
function cellCardFrom(
  row: SubsurfaceMapRow,
  cell: MapboxGeoJSONFeature,
  lngLat: [number, number]
): CellCard {
  const value = cell.properties?.value;
  return {
    kind: 'cell',
    id: row.id,
    lngLat,
    name: row.name,
    mapType: row.mapType,
    basin: row.basin,
    valueMin: row.valueMin,
    valueMax: row.valueMax,
    units: row.units,
    value: typeof value === 'number' && Number.isFinite(value) ? value : null,
  };
}

/**
 * A location as the card shows it: "31.0140°N, 104.1735°W".
 * @param {number} lng Longitude.
 * @param {number} lat Latitude.
 * @return {string} The label.
 */
export function formatLocation(lng: number, lat: number): string {
  const ns = lat >= 0 ? 'N' : 'S';
  const ew = lng >= 0 ? 'E' : 'W';
  const degrees = (value: number) =>
    formatNumber(Math.abs(value), 4, 4, 'decimal', false);
  return `${degrees(lat)}°${ns}, ${degrees(lng)}°${ew}`;
}

/**
 * A card date as the table shows it (MM/DD/YYYY), or a dash.
 * @param {string | null} iso The ISO 8601 instant.
 * @return {string} The label.
 */
const formatCardDate = (iso: string | null): string => {
  if (!iso) return '—';
  return parseMomentUtc(iso).format(US_DATE_FORMAT);
};

/**
 * Footprint hover and selection (PRD): hovering a map's footprint shows its card,
 * clicking selects the map. Over the selected map's cells the card shows the
 * value and location under the cursor instead; its cells lie under every
 * footprint, so they win wherever they are hit. A point whose topmost dataset
 * feature is a well or document is left to that feature's own popup.
 * @return {JSX.Element | null} The hover card, when a footprint or cell is hovered.
 */
export const SubsurfaceFootprintPopup = ({
  disabled = false,
}: {
  disabled?: boolean;
}) => {
  const { current: mapRef } = useMap();
  const visible = useAtomValue(datasetAtoms.onMapAtom);
  const setHighlighted = useSetAtom(datasetAtoms.highlightedItemIdAtom);
  const [card, setCard] = useState<HoverCard | null>(null);
  // The map whose cells are on the map (null while they load or when colouring
  // is off), and its row for the card's fields. The row is read through a ref:
  // it is rebuilt on every search refresh, which must not re-arm the listeners.
  const activeId = useAtomValue(subsurfaceRasterOnMapIdAtom);
  const shown = useAtomValue(selectedSubsurfaceMapAtom);
  const shownRef = useRef(shown);
  shownRef.current = shown;

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || disabled || !visible || !FILL_LAYER_ID) {
      setCard(null);
      return undefined;
    }

    // The dataset layers the second hit-test is limited to, so it never walks the
    // basemap. Refreshed on style changes rather than per move: getStyle() clones the
    // whole style. Null while the style loads, when the query falls back to all layers.
    let datasetLayers: string[] | null = null;
    const readDatasetLayers = () => {
      datasetLayers =
        styleLayerIds(map)?.filter((id) =>
          id.startsWith(DATASET_LAYER_PREFIX)
        ) ?? null;
    };
    const readDatasetLayersSoon = debounce(readDatasetLayers, 50);
    readDatasetLayers();

    // What the point belongs to: the footprint under it (the click target, and
    // the card off the selected map) and whether the selected map's cells are
    // there too (then the card is the selected map's). Both are queried first, so
    // a point off every map never runs the second query. The topmost dataset
    // feature then decides who owns the point: a well dot or document on top
    // keeps its own hover and click.
    interface Hit {
      cell: MapboxGeoJSONFeature | null;
      footprint: MapboxGeoJSONFeature | null;
    }
    const hitAt = (event: MapMouseEvent): Hit | null => {
      const footprint = map.getLayer(FILL_LAYER_ID)
        ? map.queryRenderedFeatures(event.point, { layers: [FILL_LAYER_ID] })[0]
        : undefined;
      const cell =
        activeId !== null && map.getLayer(CELLS_LAYER_ID)
          ? map.queryRenderedFeatures(event.point, {
              layers: [CELLS_LAYER_ID],
            })[0]
          : undefined;
      if (!footprint && !cell) return null;
      const layers = datasetLayers?.filter((id) => map.getLayer(id));
      // Areas draw above the footprints but never take a footprint's point: the
      // Petrotech card keeps every point it had before Areas were on the map.
      const top = map
        .queryRenderedFeatures(event.point, layers ? { layers } : undefined)
        .find(
          (feature) =>
            feature.layer.id.startsWith(DATASET_LAYER_PREFIX) &&
            !AREAS_LAYER_IDS.includes(feature.layer.id)
        );
      if (top && top.layer.id !== FILL_LAYER_ID) return null;
      return { cell: cell ?? null, footprint: footprint ?? null };
    };

    // DocumentHoverPopup clears the cursor on its own miss, and neither listener
    // order nor its throttle timer is fixed relative to ours. So the pointer is
    // claimed just past one throttle window: after any clear the same movement
    // can still fire.
    let overFootprint = false;
    let cursorTimer: ReturnType<typeof setTimeout> | undefined;
    const claimCursor = () => {
      clearTimeout(cursorTimer);
      cursorTimer = setTimeout(() => {
        if (overFootprint) map.getCanvas().style.cursor = 'pointer';
      }, CURSOR_CLAIM_DELAY_MS);
    };

    // Throttled like the documents and wells popups.
    const onMove = throttle((event: MapMouseEvent) => {
      const hit = hitAt(event);
      // A footprint under the cursor is clickable even where the selected map's
      // card shows (its cells lie under every footprint).
      overFootprint = !!hit?.footprint;
      if (!hit) {
        setCard(null);
        return;
      }
      if (overFootprint) claimCursor();
      const lngLat: [number, number] = [event.lngLat.lng, event.lngLat.lat];
      const row = shownRef.current;
      const { cell, footprint } = hit;
      if (cell && row && row.id === activeId) {
        setCard(cellCardFrom(row, cell, lngLat));
        return;
      }
      if (!footprint) {
        setCard(null);
        return;
      }
      // Same footprint: only the anchor moves, so its fields aren't rebuilt.
      const id = footprintId(footprint);
      setCard((current) => {
        if (current?.kind === 'footprint' && current.id === id) {
          return { ...current, lngLat };
        }
        return cardFrom(footprint, lngLat);
      });
    }, HOVER_THROTTLE_MS);
    const onClick = (event: MapMouseEvent) => {
      // Clicking a footprint selects it wherever it is hit, the selected map's
      // cells under it or not.
      const footprint = hitAt(event)?.footprint;
      if (footprint) setHighlighted(footprintId(footprint));
    };
    const onLeave = () => {
      onMove.cancel();
      overFootprint = false;
      setCard(null);
    };

    map.on('mousemove', onMove);
    map.on('click', onClick);
    map.on('mouseout', onLeave);
    map.on('styledata', readDatasetLayersSoon);
    return () => {
      onMove.cancel();
      readDatasetLayersSoon.cancel();
      map.off('styledata', readDatasetLayersSoon);
      clearTimeout(cursorTimer);
      map.off('mousemove', onMove);
      map.off('click', onClick);
      map.off('mouseout', onLeave);
      setCard(null);
    };
  }, [mapRef, disabled, visible, setHighlighted, activeId]);

  if (!card) return null;

  const units = card.units ? ` ${card.units}` : '';
  const range =
    card.valueMin !== null && card.valueMax !== null
      ? valueRangeLabel(card.valueMin, card.valueMax, card.units ?? undefined)
      : '—';

  let valueLabel: React.ReactNode = '—';
  if (card.kind === 'cell' && card.value !== null) {
    valueLabel = <strong>{`${formatRangeValue(card.value)}${units}`}</strong>;
  }

  const columns: [string, React.ReactNode][][] =
    card.kind === 'cell'
      ? [
          [
            ['Value', valueLabel],
            ['Map Type', card.mapType],
            ['Basin', card.basin ?? '—'],
          ],
          [
            ['Value Range', range],
            ['Location', formatLocation(card.lngLat[0], card.lngLat[1])],
          ],
        ]
      : [
          [
            ['Map Type', card.mapType],
            ['Source', card.source],
            ['Basin', card.basin ?? '—'],
            ['Interval', card.geologicInterval ?? '—'],
          ],
          [
            ['Value Range', range],
            ['Layer Type', card.layerType ?? '—'],
            ['Created', formatCardDate(card.createdAt)],
            ['Updated', formatCardDate(card.updatedAt)],
          ],
        ];

  return (
    <Popup
      longitude={card.lngLat[0]}
      latitude={card.lngLat[1]}
      closeButton={false}
      closeOnClick={false}
      className={mapStyles.popup}
      maxWidth="440px"
    >
      <PopupCard title={card.name} columns={columns} />
    </Popup>
  );
};
